/**
 * useUnreadTracker — granular per-item unread badge counter.
 *
 * Tracks which item IDs the user has *seen* (the inverse of "unread") and
 * persists that set in localStorage. The badge count is computed as the
 * number of currently-known IDs that are NOT in the seen set.
 *
 * Why "seen IDs" (not "unread IDs"):
 *   - Survives reloads naturally: re-mounting just re-applies the same
 *     "everything I haven't seen yet" rule against the latest server data.
 *   - No backend changes required.
 *   - Independent of socket events: sockets only act as a *trigger* to
 *     re-fetch the current item list sooner.
 *
 * On the very first ever use (no entry in localStorage yet) we treat all
 * currently-existing items as already seen — so a brand new browser
 * session doesn't show "247 unread" the first time. Only items that
 * *appear after* this initialization are counted as unread until the
 * user opens them and `markSeen(id)` is called.
 *
 * Usage:
 *   const { unreadCount, unreadIds, markSeen } = useUnreadTracker({
 *     storageKey: 'auto-remap.admin.unread.orders',
 *     fetcher: async () => {
 *       const r = await fetch(`${API}/api/orders/ids?limit=200`, { headers });
 *       return await r.json(); // array of plain id strings
 *     },
 *     enabled: !!token,
 *     intervalMs: 30000,
 *     refreshSignal,           // bump this to force an immediate re-fetch
 *   });
 *
 *   // …on row click:
 *   markSeen(order.id);
 *
 * The seen-set is capped at MAX_TRACKED entries (oldest evicted) so it
 * never grows without bound. We also drop IDs that are no longer in the
 * current id list — this keeps the persisted JSON tiny.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const MAX_TRACKED = 500;

const readSeen = (storageKey) => {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.ids)) return null;
    return parsed; // { initialized: true, ids: [...] }
  } catch (_) {
    return null;
  }
};

const writeSeen = (storageKey, payload) => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(payload));
  } catch (_) { /* quota / private mode — degrade silently */ }
};

export default function useUnreadTracker({
  storageKey,
  fetcher,
  enabled = true,
  intervalMs = 120000,
  refreshSignal = 0,
} = {}) {
  // currentIds = IDs returned by the latest fetch
  const [currentIds, setCurrentIds] = useState([]);
  // seenIds is mirrored in localStorage; we keep a React state copy so
  // re-renders pick up `markSeen` calls.
  const [seenSet, setSeenSet] = useState(() => {
    const persisted = readSeen(storageKey);
    if (!persisted) return new Set();
    return new Set(persisted.ids);
  });
  const initializedRef = useRef(!!readSeen(storageKey));
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // Persist a fresh seen-set, applying the cap.
  const persistSeen = useCallback((nextSet) => {
    let ids = Array.from(nextSet);
    if (ids.length > MAX_TRACKED) ids = ids.slice(-MAX_TRACKED);
    writeSeen(storageKey, { initialized: true, ids });
    return new Set(ids);
  }, [storageKey]);

  const refresh = useCallback(async () => {
    if (!enabled || !fetcherRef.current) return;
    let ids;
    try {
      ids = await fetcherRef.current();
    } catch (_) {
      return; // network blip — keep previous state
    }
    if (!Array.isArray(ids)) return;
    setCurrentIds(ids);
    if (!initializedRef.current) {
      // First ever run for this user/key — treat everything currently
      // visible as already seen so we don't blast a giant badge on day 1.
      initializedRef.current = true;
      const initialSet = new Set(ids);
      const capped = persistSeen(initialSet);
      setSeenSet(capped);
    }
  }, [enabled, persistSeen]);

  // Polling + initial load
  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    if (!intervalMs) return undefined;
    const id = setInterval(refresh, intervalMs);
    return () => clearInterval(id);
  }, [enabled, intervalMs, refresh]);

  // External trigger (socket event etc.)
  useEffect(() => {
    if (!enabled) return;
    if (refreshSignal === 0) return; // ignore the initial 0
    refresh();
  }, [refreshSignal, enabled, refresh]);

  // Cross-tab sync — if another tab marks something as seen, reflect it here.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== storageKey) return;
      const persisted = readSeen(storageKey);
      if (!persisted) return;
      setSeenSet(new Set(persisted.ids));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [storageKey]);

  const markSeen = useCallback((id) => {
    if (!id) return;
    setSeenSet((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return persistSeen(next);
    });
  }, [persistSeen]);

  const markManySeen = useCallback((ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return;
    setSeenSet((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of ids) {
        if (id && !next.has(id)) { next.add(id); changed = true; }
      }
      if (!changed) return prev;
      return persistSeen(next);
    });
  }, [persistSeen]);

  const unreadIds = useMemo(
    () => currentIds.filter((id) => !seenSet.has(id)),
    [currentIds, seenSet],
  );

  return {
    unreadCount: unreadIds.length,
    unreadIds,
    markSeen,
    markManySeen,
    refresh,
  };
}
